/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import {
  ICarbonScopeOneData,
  IDetailComponentProps,
  ISiteCard
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import CarbonScopeOneSavingsByMeterDetailNewComponent from './CarbonScopeOneSavingsByMeterDetailNewComponent';
import CarbonScopeOneSavingsByMeterDetailComponent from './CarbonScopeOneSavingsByMeterDetailComponent';

import { Card } from '@scuf/common';
import { ApiContext } from '../../../../context/ApiContext';
import { UtilityType } from '../../../shared/Utils/Constants';

interface ICarbonScope extends ISiteCard {
  data: ICarbonScopeOneData;
  UtilityBillType?: string;
  scope?: string;
}

export const CarbonScopeOneSavingsByMeterComponentContent = (
  props: ICarbonScope
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, UtilityBillType, scope } = props;
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      {toggleFeatureScopeOne && UtilityBillType !== UtilityType.Electricity ? (
        <CarbonScopeOneSavingsByMeterDetailNewComponent
          data={data}
          UtilityBillType={UtilityBillType!}
          scope={scope}
        />
      ) : (
        <CarbonScopeOneSavingsByMeterDetailComponent
          data={data}
          UtilityBillType={UtilityBillType!}
          scope={scope}
        />
      )}
    </div>
  );
};

const CarbonScopeOneSavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(CarbonScopeOneSavingsByMeterComponentContent)
);

const CarbonScopeOneSavingsByMeterComponentSeparate = (
  props: IDetailComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { UtilityBillType, dataConnector } = props;

  const onRefresh = () => dataConnector?.actions?.getAll();
  const { all, loading, error, status } = dataConnector?.data;
  const carbonScopeOneSavingsData = (all as unknown) as ICarbonScopeOneData;
  const isDataAvailable = carbonScopeOneSavingsData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonScopeOneData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(carbonScopeOneSavingsData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <Card className="Savemeter">
      <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
        {t(d.breakdownFuel)}
      </Card.Header>
      <Card.Content>
        <div className="meter-savings-Datatable detail-view">
          <CarbonScopeOneSavingsByMeterComponentWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            UtilityBillType={UtilityBillType}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default CarbonScopeOneSavingsByMeterComponentSeparate;
